import { WebAppBookNow } from "@heal-me/healme-widget"
import { Bars3Icon, XMarkIcon } from "@heroicons/react/24/solid"
import React, { useState, useEffect } from "react"
import { twMerge } from "tailwind-merge"

import { practiceDefaultServiceId } from "../../@core/practice/practice.utils"
import { scrollToId } from "../ProProfile/ScrollToSection"
import { scopePath } from "../WebsiteBuilderV2/utils/urlUtils"

import AnimatedSection from "./AnimatedSection"

const Navigation = ({ practice, theme, className, color, viewingInBuilder, ClipPath, ClipPathTwo }) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  color ||= theme.colorTextLight

  // Check if this is a v2 site - ONLY use explicit practice flags
  // Do NOT use isV2Site() as it does URL pattern matching that can incorrectly match v1 sites
  // This component is used by v1 sites, so we must be strict about v2 detection
  const isV2 = practice?.builderVersion === "v2" || practice?.builder_version === "v2"

  // Smooth scroll to anchor on page load (for v1 sites only)
  useEffect(() => {
    if (isV2) return // Only apply to v1 sites

    const handleHashScroll = () => {
      const hash = window.location.hash
      if (hash) {
        const id = hash.substring(1) // Remove the # symbol
        // Use scrollToId function for consistent behavior
        // Create a synthetic event object for scrollToId
        const syntheticEvent = {
          preventDefault: () => {}
        }
        scrollToId(syntheticEvent, id)
      }
    }

    // Handle initial load with hash
    if (window.location.hash) {
      // Wait for DOM to be fully loaded
      if (document.readyState === "complete") {
        // Small delay to ensure all content is rendered
        setTimeout(handleHashScroll, 100)
      } else {
        const handleLoad = () => {
          setTimeout(handleHashScroll, 100)
        }
        window.addEventListener("load", handleLoad)
        return () => window.removeEventListener("load", handleLoad)
      }
    }

    // Also handle hash changes (in case user navigates within the same page)
    const handleHashChange = () => {
      setTimeout(handleHashScroll, 100)
    }
    window.addEventListener("hashchange", handleHashChange)
    return () => window.removeEventListener("hashchange", handleHashChange)
  }, [isV2])

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen)
  }

  // For v2 sites, use header logo from theme/practice config, otherwise use imageAssets
  const logoUrl = isV2
    ? practice?.logoUrl ||
      theme?.header?.logo ||
      practice.imageAssets.find((asset) => asset.description === "logo")?.resizedImageUrl
    : practice.imageAssets.find((asset) => asset.description === "logo")?.resizedImageUrl

  // Helper to handle navigation - use URLs for v2 sites, navigate to root with anchor for v1
  const handleNavigation = (e, target) => {
    if (isV2) {
      e.preventDefault()
      let path = "/"
      if (target === "home") {
        path = "/"
      } else if (target === "services") {
        path = "/services"
      } else if (target === "reviews") {
        path = "/review" // v2 sites use /review (singular)
      } else if (target === "about") {
        path = "/about"
      }
      window.location.href = scopePath(path, practice)
    } else {
      e.preventDefault()
      // For v1 sites, navigate to root with anchor (e.g., /practice-slug#about)
      const rootPath = scopePath("/", practice)
      const anchor = target === "home" ? "" : `#${target}`
      window.location.href = `${rootPath}${anchor}`
    }
  }

  // For mobile menu logo, use header logo for v2 sites
  const mobileLogoUrl = isV2 ? practice?.logoUrl || theme?.header?.logo || practice.logo : practice.logo

  return (
    <AnimatedSection
      style={{ color }}
      className={twMerge(
        "absolute top-0 z-10 flex w-full items-center justify-between px-12 py-6 delay-[250ms] md:p-4",
        className
      )}>
      <div className="text-left">
        {logoUrl ? (
          isV2 ? (
            <a href={scopePath("/", practice)} className="cursor-pointer transition-opacity hover:opacity-75">
              <img
                src={logoUrl}
                alt={`${practice.user?.name || "Practice"} logo`}
                className="max-h-24  max-w-24 md:max-w-[75%]"
              />
            </a>
          ) : (
            <img
              src={logoUrl}
              alt={`${practice.user?.name || "Practice"} logo`}
              className="max-h-32 max-w-[360px] md:max-w-[75%]"
            />
          )
        ) : (
          <div
            className="text-[28px] md:text-lg"
            style={{ fontFamily: theme.fontHeading, fontWeight: theme.fontHeadingWeight }}>
            {practice.user.name}
          </div>
        )}
      </div>
      <div className="flex gap-8 md:hidden">
        {isV2 && (
          <a
            href={scopePath("/", practice)}
            className="inline-flex items-center border-b border-transparent transition duration-150 ease-out hover:border-[var(--hover-color)] active:border-b-2"
            style={{ "--hover-color": theme.colorAccent }}>
            Home
          </a>
        )}
        <button
          className="inline-flex items-center border-b border-transparent transition duration-150 ease-out hover:border-[var(--hover-color)] active:border-b-2"
          style={{ "--hover-color": theme.colorAccent }}
          onClick={(e) => handleNavigation(e, "about")}>
          About
        </button>
        <button
          className="inline-flex items-center border-b border-transparent transition duration-150 ease-out hover:border-[var(--hover-color)] active:border-b-2"
          style={{ "--hover-color": theme.colorAccent }}
          onClick={(e) => handleNavigation(e, "services")}>
          Services
        </button>
        <button
          className="inline-flex items-center border-b border-transparent transition duration-150 ease-out hover:border-[var(--hover-color)] active:border-b-2"
          style={{ "--hover-color": theme.colorAccent }}
          onClick={(e) => handleNavigation(e, "reviews")}>
          Reviews
        </button>
        <button
          className="inline-flex items-center border-b border-transparent transition duration-150 ease-out hover:border-[var(--hover-color)] active:border-b-2"
          style={{ "--hover-color": theme.colorAccent }}
          onClick={(e) => handleNavigation(e, "about")}>
          Credentials
        </button>
        {isV2 ? (
          <a
            href={scopePath("/services", practice)}
            className={twMerge(
              "border bg-transparent px-8 py-2 transition duration-150 ease-out hover:border-[var(--hover-color)] hover:bg-[var(--hover-color)]",
              theme.buttonStyle === "rounded" ? "rounded-full" : "rounded",
              viewingInBuilder ? "pointer-events-none" : ""
            )}
            style={{ "--hover-color": theme.colorAccent, borderColor: color }}>
            Book Now
          </a>
        ) : (
          <WebAppBookNow
            practiceId={practice.id}
            serviceId={practiceDefaultServiceId(practice)}
            buttonCopy="Book Now"
            buttonClasses={twMerge(
              "border bg-transparent px-8 py-2 transition duration-150 ease-out hover:border-[var(--hover-color)] hover:bg-[var(--hover-color)]",
              theme.buttonStyle === "rounded" ? "rounded-full" : "rounded",
              viewingInBuilder ? "pointer-events-none" : ""
            )}
            style={{ "--hover-color": theme.colorAccent, borderColor: color }}
          />
        )}
      </div>
      {/* Mobile Navigation */}
      <div className="hidden md:block">
        <button onClick={toggleMenu}>
          <Bars3Icon className="h-6 w-6" />
        </button>
        <div
          style={{
            backgroundColor: isV2
              ? theme?.colors?.background || theme?.colors?.bgLight || theme?.colorBgLight || "#FFFFFF"
              : theme.colorBgLight || "#FFFFFF",
            color: isV2
              ? theme?.colors?.textDark || theme?.colorTextDark || "#333333"
              : theme.colorTextDark || "#333333"
          }}
          className={`fixed right-0 top-0 z-50 flex h-screen w-full flex-col items-center justify-center gap-8 overflow-hidden transition-transform duration-300 ease-in-out md:px-5 ${
            isMenuOpen ? "translate-x-0" : "translate-x-full"
          }`}>
          <div className="absolute right-4 top-4 z-10">
            <XMarkIcon className="h-6 w-6 cursor-pointer" onClick={toggleMenu} />
          </div>
          <div>
            {mobileLogoUrl ? (
              <img
                src={mobileLogoUrl}
                alt={`${practice.user?.name || "Practice"} logo`}
                className="max-h-12 max-w-[180px]"
              />
            ) : (
              <div className="text-lg font-semibold">{practice.user.name}</div>
            )}
            <div className="mt-2 text-sm" style={{ color: theme.colorGrey }}>
              {practice.user.title}
            </div>
          </div>
          <div className="flex flex-col items-center justify-center gap-4">
            {isV2 && (
              <a href={scopePath("/", practice)} onClick={toggleMenu}>
                Home
              </a>
            )}
            <button
              onClick={(e) => {
                handleNavigation(e, "about")
                toggleMenu()
              }}>
              About
            </button>
            <button
              onClick={(e) => {
                handleNavigation(e, "services")
                toggleMenu()
              }}>
              Services
            </button>
            <button
              onClick={(e) => {
                handleNavigation(e, "reviews")
                toggleMenu()
              }}>
              Reviews
            </button>
            <button
              onClick={(e) => {
                handleNavigation(e, "about")
                toggleMenu()
              }}>
              Credentials
            </button>
          </div>
          <div className="w-full">
            {isV2 ? (
              <a
                href={scopePath("/services", practice)}
                onClick={toggleMenu}
                className={twMerge(
                  "block w-full border px-8 py-2 text-center",
                  theme.buttonStyle === "rounded" ? "rounded-full" : "rounded",
                  viewingInBuilder ? "pointer-events-none" : ""
                )}
                style={{
                  color: theme.colorTextLight,
                  backgroundColor: theme.colorAccent
                }}>
                Book Now
              </a>
            ) : (
              <WebAppBookNow
                practiceId={practice.id}
                serviceId={practice.defaultServiceId}
                buttonCopy="Book Now"
                buttonClasses={twMerge(
                  "w-full border px-8 py-2",
                  theme.buttonStyle === "rounded" ? "rounded-full" : "rounded",
                  viewingInBuilder ? "pointer-events-none" : ""
                )}
                style={{
                  color: theme.colorTextLight,
                  backgroundColor: theme.colorAccent
                }}
              />
            )}
          </div>
          {ClipPath && <ClipPath />}
          {ClipPathTwo && <ClipPathTwo />}
        </div>
      </div>
    </AnimatedSection>
  )
}

export default Navigation
